<template>
  <div class="page-container">
    <div v-if="loading" class="full-loading">
      <div class="spinner"></div>
    </div>

    <div v-else-if="ticket" class="content-wrapper">
      <header class="page-header">
        <div class="header-breadcrumb">
          <span class="muted" @click="router.push('/')" style="cursor: pointer;">Support</span> / 
          <span class="active">Ticket #{{ ticket.id }}</span>
        </div>
        <div class="header-actions" v-if="isAdminOrAgent">
             <select v-model="ticket.status" @change="handleStatusChange" class="status-select">
                <option value="NEW">New</option>
                <option value="ASSIGNED">Assigned</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="PENDING_USER">Pending User</option>
                <option value="RESOLVED">Resolved</option>
                <option value="CLOSED">Closed</option>
             </select>
        </div>
      </header>

      <div class="detail-grid">
        <div class="main-column">
          <div class="glass-card main-ticket-card">
            <div class="ticket-header">
              <div class="creator-avatar">
                {{ (ticket.creator?.full_name || 'U').charAt(0) }}
              </div>
              <div class="ticket-meta">
                <h2>{{ ticket.title }}</h2>
                <span class="date-posted">Posted on {{ new Date(ticket.created_at).toLocaleString() }} by {{ ticket.creator?.full_name || 'Unknown' }}</span>
              </div>
            </div>
            <div class="ticket-body">
              <p v-html="formatComment(ticket.description || 'No description provided.')"></p>
            </div>
          </div>

          <div class="comments-section">
            <h3 class="section-title">Replies ({{ ticket.comments?.length || 0 }})</h3>
            
            <div class="comments-list">
              <div v-for="c in ticket.comments" :key="c.id" class="glass-card comment-card">
                <div class="comment-avatar">{{ c.author_name?.charAt(0) }}</div>
                <div class="comment-content">
                  <div class="comment-meta">
                    <strong>{{ c.author_name }}</strong>
                    <span class="time">{{ new Date(c.created_at).toLocaleString() }}</span>
                  </div>
                  <p class="comment-text" v-html="formatComment(c.content)"></p>
                </div>
              </div>
            </div>

            <div class="glass-card reply-card">
              <textarea v-model="commentText" placeholder="Write your reply here..."></textarea>
              <div class="reply-footer">
                <div class="file-upload-wrapper">
                  <label class="file-label">
                    <input type="file" @change="handleFileSelect" style="display: none;" />
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
                    Attach File
                  </label>
                  <span v-if="selectedFile" class="file-name">{{ selectedFile.name }} <button @click="selectedFile = null" class="clear-file">×</button></span>
                </div>
                <button @click="submitComment" :disabled="(!commentText.trim() && !selectedFile) || submitting" class="btn-primary">
                  {{ submitting ? 'Sending...' : 'Send Reply' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="side-column">
          <div class="glass-card side-widget">
            <h3 class="widget-title">Ticket Info</h3>
            
            <div class="info-group">
              <label>Status</label>
              <span :class="['status-badge', ticket.status.toLowerCase()]">{{ ticket.status.replace('_', ' ') }}</span>
            </div>

            <div class="info-group">
              <label>Category</label>
              <span class="info-val">{{ ticket.category || 'Others' }}</span>
            </div>

            <div class="info-group">
              <label>Urgency</label>
              <span class="info-val">{{ ticket.urgency || 'Medium' }}</span>
            </div>
            
            <div class="info-group">
              <label>Priority</label>
              <span :class="['priority-label', ticket.priority.toLowerCase()]">{{ ticket.priority }}</span>
            </div>
            
            <div class="info-group">
              <label>SLA Deadline</label>
              <span class="deadline-text" :class="{ overdue: isOverdue(ticket.due_date) && ticket.status !== 'RESOLVED' && ticket.status !== 'CLOSED' }">
                {{ formatFullDate(ticket.due_date) }}
              </span>
            </div>
          </div>

          <div class="glass-card side-widget">
            <h3 class="widget-title">Assignment</h3>
            <div class="info-group">
              <label>Assigned To</label>
              <div class="assignee-box" v-if="ticket.assignee">
                <div class="mini-avatar">{{ ticket.assignee?.full_name?.charAt(0) }}</div>
                <span>{{ ticket.assignee?.full_name }}</span>
              </div>
              <span v-else class="unassigned">Unassigned</span>
            </div>
          </div>

          <!-- Timeline Widget -->
          <div class="glass-card side-widget timeline-widget">
            <h3 class="widget-title">History Timeline</h3>
            <div class="timeline-container">
              <div v-for="h in history" :key="h.id" class="timeline-item">
                <div class="timeline-marker"></div>
                <div class="timeline-content">
                  <div class="timeline-time">{{ new Date(h.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }}</div>
                  <div class="timeline-action">
                    <strong>{{ h.actor_name || 'System' }}</strong>
                    <span v-if="h.action === 'STATUS_CHANGE'"> changed status to <b>{{ h.new_value }}</b></span>
                    <span v-else-if="h.action === 'ASSIGNED'"> {{ h.new_value }}</span>
                    <span v-else> {{ h.new_value }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="error-state">
      <h2>Ticket Not Found</h2>
      <p>The ticket you are looking for does not exist or has been deleted.</p>
      <button @click="router.push('/')" class="btn-primary">Return to Dashboard</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import axios from 'axios'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const ticket = ref<any>(null)
const history = ref<any[]>([])
const loading = ref(true)
const submitting = ref(false)
const commentText = ref('')
const selectedFile = ref<File | null>(null)

// FIX: Ensure API_URL is strictly using the production worker URL to avoid localhost fallback errors
const API_URL = import.meta.env.VITE_API_URL || 'https://it-ticketing-api.maulanarofiq72.workers.dev'

const isAdminOrAgent = computed(() => {
  const role = (authStore.user as any)?.role
  return role === 'ADMIN' || role === 'AGENT'
})

const fetchTicket = async () => {
  try {
    const [ticketRes, historyRes] = await Promise.all([
      axios.get(`${API_URL}/tickets/${route.params.id}`),
      axios.get(`${API_URL}/tickets/${route.params.id}/history`)
    ])
    ticket.value = ticketRes.data
    history.value = historyRes.data
  } catch (err) {
    console.error('Fetch Error', err)
  } finally {
    loading.value = false
  }
}

const handleFileSelect = (event: any) => {
  const file = event.target.files[0]
  if (file) {
    // Optional: add file size validation here (e.g., max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB')
      return
    }
    selectedFile.value = file
  }
}

const submitComment = async () => {
  submitting.value = true
  try {
    if (selectedFile.value) {
      const formData = new FormData()
      formData.append('content', commentText.value)
      formData.append('file', selectedFile.value)
      
      await axios.post(`${API_URL}/tickets/${ticket.value.id}/comments`, formData)
    } else {
      await axios.post(`${API_URL}/tickets/${ticket.value.id}/comments`, {
        content: commentText.value
      })
    }
    commentText.value = ''
    selectedFile.value = null
    await fetchTicket()
  } catch (err) {
    alert('Failed to post comment')
  } finally {
    submitting.value = false
  }
}

const handleStatusChange = async () => {
  try {
    await axios.put(`${API_URL}/tickets/${ticket.value.id}`, {
      status: ticket.value.status
    })
  } catch (err) {
    alert('Failed to update status')
    fetchTicket()
  }
}

const formatFullDate = (date: string) => {
  if (!date) return 'Not set'
  return new Date(date).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })
}

const isOverdue = (date: string) => date ? new Date(date) < new Date() : false

const formatComment = (text: string) => {
  if (!text) return ''
  // Basic replacement for 📎 [Attachment: filename](url) - Cache buster 1
  let formatted = text
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>')
    
  // Explicitly match the pattern backend creates: 📎 [Attachment: fileName](/api/attachments/fileKey)
  const attachmentRegex = /📎 \[Attachment: ([^\]]+)\]\(([^)]+)\)/g;
  formatted = formatted.replace(attachmentRegex, (_match, fileName, fileUrl) => {
    const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API_URL}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
    return `<br><a href="${fullUrl}" target="_blank" class="attachment-link" style="color: #2563eb; text-decoration: underline; font-weight: 600;">📎 Download ${fileName}</a>`;
  });
  
  return formatted;
}

onMounted(fetchTicket)
</script>

<style scoped>
.page-container { padding: 2rem; max-width: 1400px; margin: 0 auto; }

.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; }
.header-breadcrumb { font-weight: 600; font-size: 1.1rem; }
.header-breadcrumb .muted { color: var(--text-muted); }
.header-breadcrumb .active { color: var(--primary); }

.status-select { 
  padding: 0.6rem 1.25rem; border-radius: 20px; border: 1px solid var(--border); 
  font-weight: 600; background: white; cursor: pointer; outline: none;
}
.status-select:focus { border-color: var(--accent); }

.detail-grid { display: grid; grid-template-columns: 1fr 320px; gap: 2rem; }

.main-ticket-card { padding: 2rem; margin-bottom: 2rem; }
.ticket-header { display: flex; gap: 1.5rem; margin-bottom: 1.5rem; align-items: center; }
.creator-avatar { width: 50px; height: 50px; background: var(--primary); color: white; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; font-weight: 800; }
.ticket-meta h2 { margin: 0 0 0.25rem 0; font-size: 1.5rem; color: var(--primary); }
.date-posted { font-size: 0.85rem; color: var(--text-muted); }
.ticket-body { color: var(--text-main); line-height: 1.6; font-size: 1.05rem; }

.comments-section { margin-top: 3rem; }
.section-title { font-size: 1.25rem; font-weight: 800; color: var(--primary); margin-bottom: 1.5rem; }

.comments-list { display: flex; flex-direction: column; gap: 1rem; margin-bottom: 2rem; }
.comment-card { padding: 1.5rem; display: flex; gap: 1rem; }
.comment-avatar { width: 40px; height: 40px; background: #f1f5f9; color: var(--text-main); border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: 700; }
.comment-content { flex: 1; }
.comment-meta { display: flex; justify-content: space-between; margin-bottom: 0.5rem; }
.comment-meta strong { color: var(--primary); }
.comment-meta .time { font-size: 0.8rem; color: var(--text-muted); }
.comment-content p { margin: 0; color: var(--text-main); line-height: 1.5; }

.reply-card { padding: 1.5rem; }
.reply-card textarea { width: 100%; min-height: 100px; border: 1px solid var(--border); border-radius: 12px; padding: 1rem; font-family: inherit; font-size: 1rem; resize: vertical; margin-bottom: 1rem; transition: border-color 0.2s; }
.reply-card textarea:focus { outline: none; border-color: var(--accent); }
.reply-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 1rem; }
.file-upload-wrapper { display: flex; align-items: center; gap: 1rem; }
.file-label { display: flex; align-items: center; gap: 0.5rem; color: var(--text-muted); cursor: pointer; font-weight: 600; font-size: 0.9rem; transition: color 0.2s; }
.file-label:hover { color: var(--accent); }
.file-name { font-size: 0.8rem; background: #f1f5f9; padding: 0.3rem 0.6rem; border-radius: 6px; display: flex; align-items: center; gap: 0.5rem; }
.clear-file { background: none; border: none; color: #ef4444; cursor: pointer; font-weight: bold; font-size: 1rem; padding: 0; line-height: 1; }
.btn-primary { background: var(--primary); color: white; border: none; padding: 0.75rem 2rem; border-radius: 20px; font-weight: 600; cursor: pointer; transition: opacity 0.2s; }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

.side-column { display: flex; flex-direction: column; gap: 1.5rem; }
.side-widget { padding: 1.5rem; }
.widget-title { font-size: 1.1rem; margin: 0 0 1.5rem 0; color: var(--primary); }

.info-group { margin-bottom: 1.25rem; display: flex; flex-direction: column; gap: 0.5rem; }
.info-group:last-child { margin-bottom: 0; }
.info-group label { font-size: 0.85rem; color: var(--text-muted); font-weight: 600; text-transform: uppercase; }
.info-val { font-weight: 600; color: var(--text-main); }

.status-badge { padding: 0.4rem 1rem; border-radius: 20px; font-size: 0.75rem; font-weight: 700; text-transform: capitalize; width: fit-content; }
.status-badge.new { background: #f1f5f9; color: #475569; }
.status-badge.assigned { background: #e0f2fe; color: #0369a1; }
.status-badge.pending_user { background: #fef3c7; color: #b45309; }
.status-badge.open { background: #eff6ff; color: #2563eb; }
.status-badge.in_progress { background: #fffbeb; color: #d97706; }
.status-badge.resolved { background: #f0fdf4; color: #16a34a; }
.status-badge.closed { background: #f1f5f9; color: #475569; }

/* Timeline Styles */
.timeline-container { position: relative; padding-left: 1rem; margin-top: 0.5rem; }
.timeline-container::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 2px; background: #e2e8f0; }
.timeline-item { position: relative; padding-bottom: 1.5rem; }
.timeline-marker { position: absolute; left: -1.35rem; top: 0.25rem; width: 10px; height: 10px; border-radius: 50%; background: #3b82f6; border: 2px solid white; z-index: 1; }
.timeline-content { display: flex; flex-direction: column; gap: 0.2rem; }
.timeline-time { font-size: 0.75rem; color: #94a3b8; font-weight: 600; }
.timeline-action { font-size: 0.85rem; color: #475569; line-height: 1.4; }
.timeline-action b { color: #0f172a; }

.priority-label { font-size: 0.7rem; font-weight: 800; text-transform: uppercase; padding: 0.2rem 0.6rem; border-radius: 6px; width: fit-content; }
.priority-label.critical { background: #fee2e2; color: #991b1b; }
.priority-label.high { background: #ffedd5; color: #9a3412; }
.priority-label.medium { background: #dcfce7; color: #166534; }
.priority-label.low { background: #dbeafe; color: #1e40af; }

.deadline-text { font-weight: 600; color: var(--text-main); }
.deadline-text.overdue { color: #dc2626; font-weight: 700; }

.assignee-box { display: flex; align-items: center; gap: 0.75rem; font-weight: 600; color: var(--text-main); }
.mini-avatar { width: 32px; height: 32px; border-radius: 8px; background: var(--accent); color: white; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; }
.unassigned { color: var(--text-muted); font-style: italic; }

.full-loading { text-align: center; padding: 10rem 0; }
.spinner { width: 40px; height: 40px; border: 4px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto; }
@keyframes spin { to { transform: rotate(360deg); } }

.error-state { text-align: center; padding: 5rem; }
.error-state h2 { color: var(--primary); margin-bottom: 1rem; }

@media (max-width: 900px) {
  .detail-grid { grid-template-columns: 1fr; }
}
</style>
